import React, { ReactNode, useRef } from 'react';
import { MDXProvider } from '@mdx-js/react';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import NavBar from './NavBar';
import TableOfContents from './TableOfContents';

// Function to get the MDX components with the `ref` bound to headings
const getComponents = (
  setHeadingRef: (element: HTMLHeadingElement | null) => void,
) => ({
  h1: (props: any) => (
    <h1
      {...props}
      ref={setHeadingRef}
      className="text-5xl font-bold my-8 mt-16"
    >
      {props.children}
    </h1>
  ),
  h2: (props: any) => (
    <h2 {...props} ref={setHeadingRef} className="text-4xl font-bold my-8">
      {props.children}
    </h2>
  ),
  h2: (props: any) => (
    <h2
      {...props}
      // ref={setHeadingRef}
      className="text-2xl font-semibold my-4 mt-8"
    >
      {props.children}
    </h2>
  ),
  li: (props: any) => (
    <li {...props} className="list-disc text-lg ml-8">
      {props.children}
    </li>
  ),
  a: (props: { children: ReactNode; href: string }) => (
    <a className="text-lg text-blue" href={props.href}>
      {props.children}
    </a>
  ),
  p: (props: any) => (
    <p {...props} className="text-xl my-4">
      {props.children}
    </p>
  ),
  img: (props: { src: string; alt?: string; image?: IGatsbyImageData }) => {
    const { src, alt = '', image } = props;
    return image ? (
      <GatsbyImage image={image} alt={alt} />
    ) : (
      <img src={src} alt={alt} style={{ width: '100%', height: 'auto' }} />
    );
  },
  table: (props: any) => (
    <div className="overflow-x-auto my-8 border rounded-lg">
      <table {...props} className="min-w-full divide-y shadow-sm">
        {props.children}
      </table>
    </div>
  ),
  thead: (props: any) => (
    <thead {...props} className="text-white bg-grey">
      {props.children}
    </thead>
  ),
  tbody: (props: any) => (
    <tbody {...props} className="divide-y bg-white">
      {props.children}
    </tbody>
  ),
  tr: (props: any) => (
    <tr {...props} className="hover:bg-lightgrey transition-colors">
      {props.children}
    </tr>
  ),
  th: (props: any) => (
    <th {...props} className="px-6 py-4 text-left font-bold uppercase">
      {props.children}
    </th>
  ),
  td: (props: any) => (
    <td {...props} className="px-6 py-4 text-lg">
      {props.children}
    </td>
  ),
});

const Layout = ({
  children,
  pageContext,
}: {
  children: ReactNode;
  pageContext: any;
}) => {
  const headingsRef = useRef<HTMLHeadingElement[]>([]);

  // Callback function to collect heading elements
  const setHeadingRef = (element: HTMLHeadingElement | null) => {
    if (element && !headingsRef.current.includes(element)) {
      headingsRef.current.push(element);
    }
  };

  const components = getComponents(setHeadingRef);

  return (
    <main>
      <NavBar />
      <div className="min-h-screen">
        <div className="flex flex-col lg:flex-row">
          {/* Left Column: Document Content */}
          <div className="w-full lg:w-[calc(100%-300px)] border-r border-linegrey">
            <div className="bg-white">
              <div className="p-8 lg:p-16 lg:px-32 max-w-6xl">
                <h1 id="top" className="text-6xl font-bold mb-16 mt-0">
                  {pageContext.frontmatter.title}
                </h1>
                <div className="prose lg:prose-xl">
                  {/* Provide components with refs through MDXProvider */}
                  {/* @ts-ignore */}
                  <MDXProvider components={components}>{children}</MDXProvider>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Table of Contents */}
          <aside className="lg:w-[500px] w-full lg:px-8 lg:sticky lg:top-0 lg:h-screen lg:overflow-y-auto lg:self-start">
            <div className="px-0 py-12">
              <TableOfContents
                toc={pageContext.tableOfContents}
                frontmatter={pageContext.frontmatter}
                headingsRef={headingsRef}
              />
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default Layout;
